import { ChevronUp } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "../../../hooks/useToast";
import { tripsApi, getErrorMessage } from "../../../api";

// Components
import FormCard from "../../../components/FormCard";
import RoundedEdgeButton from "../../../components/RoundedEdgeButton";

// Utils
import { subscribeToPush } from "../../../utils/pushHelper";

// Styles
import Carousel from "../../../components/Carousel";
import LoadingBar from "../../../components/LoadingBar";
import RouteConnector from "../../../components/RouteConnector";
import classes from "./SearchingDriver.module.scss";

// Constants
const POLLING_INTERVAL = 5000; // 5 seconds

const SearchingDriver = () => {
  const navigate = useNavigate();
  const { tripId } = useParams();
  const { openToast } = useToast();

  // State Management
  const [tripDetails, setTripDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [rebookKey, setRebookKey] = useState(0);
  const [searchPhase, setSearchPhase] = useState(1);

  // Event Handlers
  const handleCancel = useCallback(async () => {
    try {
      await tripsApi.updateTrip(tripId, { status: "CANCELLED" });
    } catch (error) {
      console.error("Error cancelling trip:", error);
    }
    navigate("/booking");
  }, [tripId, navigate]);

  const handleRebook = useCallback(() => {
    setRebookKey(prev => prev + 1);
    setSearchPhase(1);
  }, []);

  // Effects
  // Subscribe to push notifications for trip updates
  useEffect(() => {
    if (!tripId) return;

    subscribeToPush();
  }, [tripId]);

  // Phase transition timer (1 -> 2 after 5 seconds)
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchPhase(2);
    }, 5000);
    return () => clearTimeout(timer);
  }, [rebookKey]);

  // Fetch initial trip details
  useEffect(() => {
    if (!tripId) return;

    const fetchTripDetails = async () => {
      try {
        setIsLoading(true);
        const data = await tripsApi.getTrip(tripId);

        setTripDetails({
          name: data?.name || "Unknown",
          from_location: data?.from_location || "Unknown",
          to_location: data?.to_location || "Unknown",
          pax: data?.pax || "",
          prefer_private: data?.prefer_private || false,
          request_female: data?.request_female || false,
          price_offer: data?.price_offer || "",
          is_peak_hour: data?.is_peak_hour || false,
        });
      } catch (error) {
        console.error("Error fetching trip details:", error);
        const message = getErrorMessage(error);
        openToast(message || "Failed to fetch trip details", 5000, "danger");
      } finally {
        setIsLoading(false);
      }
    };

    fetchTripDetails();
  }, [tripId, openToast]);

  // Poll trip status for updates
  useEffect(() => {
    if (!tripId) return;

    const checkTripStatus = async () => {
      try {
        const data = await tripsApi.getTrip(tripId);

        // Handle status changes
        if (data.status === "COMPLETED" || data.status === "CANCELLED") {
          navigate("/booking");
        } else if (data.status === "PENDING_ACCEPT") {
          navigate(`/driver-found/${tripId}`);
        }
      } catch (error) {
        console.error("Error fetching trip status:", error);
        const message = getErrorMessage(error);
        openToast(message || "Failed to fetch trip status", 5000, "danger");
      }
    };

    const interval = setInterval(checkTripStatus, POLLING_INTERVAL);

    return () => clearInterval(interval);
  }, [tripId, navigate, openToast]);

  // Loading state
  if (isLoading || !tripDetails) {
    return (
      <div className={classes.container}>
        <div className={classes.innerCardContent}>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      {/* <div className={classes.imageBackground}>
        <img src={backgroundMap} alt="background map" />
      </div> */}
      <div className={classes.innerCardContent}>
        <div className={classes.titleSection}>
          <p className={classes.title}>
            {searchPhase === 1 ? "Looking for driver" : "Connecting you with drivers..."}
          </p>
          <p className={classes.subtitle}>
            {searchPhase === 1 ? "Please wait..." : "Anytime soon!"}
          </p>
          <div className={classes.loadingBarWrapper}>
            <LoadingBar
              key={`bar1-${rebookKey}`}
              fakeLoading={searchPhase === 1}
              speed={45} // ~5 seconds (slightly less than 50 to ensure it finishes comfortably)
              maxProgress={100}
              progress={searchPhase > 1 ? 100 : 0}
            />
            <LoadingBar
              key={`bar2-${rebookKey}-${searchPhase}`}
              fakeLoading={searchPhase === 2}
              speed={200}
              maxProgress={95}
              progress={0}
            />
          </div>
        </div>

        {/* trip info card */}
        <FormCard className={classes.orderCard}>
          <div className={classes.cardContent}>
            <div className={classes.locationsImage}>
              <RouteConnector />
            </div>
            <div className={classes.rightContent}>
              <div className={classes.row}>
                <div className={classes.locationBox}>
                  <p className={classes.locationAddress}>
                    {tripDetails.from_location}
                  </p>
                </div>
                <div className={classes.paxInfo}>
                  <p className={classes.pax}>
                    PAX {tripDetails.pax}
                  </p>
                </div>
              </div>
              <hr className={classes.separator} />
              <div className={classes.row}>
                <div className={classes.locationBox}>
                  <p className={classes.locationAddress}>
                    {tripDetails.to_location}
                  </p>
                </div>
                <div className={classes.priceInfo}>
                  <div className={classes.priceWithIcon}>
                    {tripDetails.is_peak_hour && (
                      <div className={classes.peakArrows}>
                        <ChevronUp size={16} className={classes.arrow1} />
                        <ChevronUp size={16} className={classes.arrow2} />
                        <ChevronUp size={16} className={classes.arrow3} />
                      </div>
                    )}
                    <p className={classes.price}>
                      RM {Number(tripDetails.price_offer).toFixed(2)}
                    </p>
                  </div>
                  <p className={classes.priceLabel}>
                    {Number(tripDetails.price_offer) > 3 ? "Negotiable" : "Fixed"}
                  </p>
                </div>
              </div>
            </div>
          </div>
          {/* Action Buttons */}
          <div className={classes.actionButtons}>
            <RoundedEdgeButton
              className={`rejectButtonOutline ${classes.cancelButton}`}
              onClick={handleCancel}
            >
              CANCEL
            </RoundedEdgeButton>
            <RoundedEdgeButton
              className={classes.rebookButton}
              onClick={handleRebook}
            >
              RE-BOOK
            </RoundedEdgeButton>
          </div>
        </FormCard>

        {tripDetails.is_peak_hour && (
          <p className={classes.peakHourNote}>
            Price may be affected due to peak hours
          </p>
        )}
      </div>
      <Carousel />
    </div>
  );
};

export default SearchingDriver;
